import React, { useState, useEffect } from 'react'
import { message, Pagination, Modal, Select } from 'antd'
import {
    getSubscriptions,
    getUsers
} from '../../../Web Apis/webApis'
import Load from '../../Load/Load'
import { AiOutlineEye } from "react-icons/ai";
import { FaSearch } from 'react-icons/fa';
import { BsCreditCard } from 'react-icons/bs';

const { Option } = Select;

const parseGoogleMoney = (money) => {
    if (!money || typeof money !== 'object') return null
    const units = Number(money.units || 0)
    const nanos = Number(money.nanos || 0)
    const amount = units + nanos / 1e9
    if (!Number.isFinite(amount) || amount <= 0) return null
    const currency = String(money.currencyCode || '').trim().toUpperCase()
    return { amount, currency: currency || null }
}

const formatMoneyLabel = (amount, currency) => {
    const amt = Number(amount)
    if (!Number.isFinite(amt) || amt <= 0) return null
    const cur = String(currency || '').trim().toUpperCase()
    const num = amt % 1 === 0 ? String(Math.round(amt)) : amt.toFixed(2).replace(/\.?0+$/, '')
    if (cur === 'INR') return `₹${num}`
    if (cur) return `${cur} ${num}`
    return num
}

const resolveSubscriptionPrice = (subscription) => {
    if (subscription?.displayPrice?.label) {
        return subscription.displayPrice
    }

    const raw = subscription?.payment?.raw
    const storeLabel = typeof raw?.storePriceLabel === 'string' ? raw.storePriceLabel.trim() : ''
    const amount = subscription?.payment?.amount
    const currency = subscription?.payment?.currency

    if (storeLabel) {
        return {
            label: storeLabel,
            amount: typeof amount === 'number' && amount > 0 ? amount : null,
            source: 'store_recorded',
        }
    }

    if (typeof amount === 'number' && amount > 0) {
        return {
            label: formatMoneyLabel(amount, currency) || `${amount}`,
            amount,
            source: 'recorded',
        }
    }

    const purchasePrice = subscription?.storeLive?.data?.purchasePrice
    if (purchasePrice?.amount > 0) {
        return {
            label: formatMoneyLabel(purchasePrice.amount, purchasePrice.currency) || `${purchasePrice.amount}`,
            amount: Number(purchasePrice.amount),
            source: purchasePrice.source || 'google_play_v1',
        }
    }

    const liveMoney = subscription?.storeLive?.data?.autoRenewingPlan?.recurringPrice
    const parsed = parseGoogleMoney(liveMoney)
    if (parsed) {
        return {
            label: formatMoneyLabel(parsed.amount, parsed.currency) || `${parsed.amount}`,
            amount: parsed.amount,
            source: 'google_live',
        }
    }

    const planStoreAmount = Number(subscription?.plan?.storePriceAmount)
    const planStoreCurrency = subscription?.plan?.storePriceCurrency
    const planStoreLabel = subscription?.plan?.storePriceLabel
    if (planStoreAmount > 0) {
        return {
            label: planStoreLabel || formatMoneyLabel(planStoreAmount, planStoreCurrency) || `${planStoreAmount}`,
            amount: planStoreAmount,
            source: 'store_offer_sync',
        }
    }

    return { label: 'N/A', amount: null, source: 'unknown' }
}

const resolveSubscriptionRevenue = (subscription) => {
    const resolved = resolveSubscriptionPrice(subscription)
    const amount = Number(resolved?.amount || 0)
    if (resolved?.source === 'unknown' || !Number.isFinite(amount) || amount <= 0) {
        return 0
    }
    return amount
}

const AdminSubscriptions = () => {
    const [subscriptions, setSubscriptions] = useState([])
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(false)
    const [showViewSubscription, setShowViewSubscription] = useState(false)
    const [selectedSubscription, setSelectedSubscription] = useState({})
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage] = useState(10)
    const [searchQuery, setSearchQuery] = useState("")
    const [statusFilter, setStatusFilter] = useState("")

    const isStorePurchase = (subscription) => {
        const provider = String(subscription?.payment?.provider || '').toLowerCase()
        return provider === 'google' || provider === 'apple'
    }

    const storeSubscriptions = subscriptions.filter(isStorePurchase)
    const originalStoreProducts = Array.from(
        new Set(
            storeSubscriptions
                .map((s) => s?.iap?.productId || s?.iap?.subscriptionId)
                .filter(Boolean)
        )
    )
    const productPurchaseCounts = storeSubscriptions.reduce((acc, s) => {
        const key = s?.iap?.productId || s?.iap?.subscriptionId || 'unknown'
        acc[key] = (acc[key] || 0) + 1
        return acc
    }, {})
    const storePlanView = Object.values(
        storeSubscriptions.reduce((acc, s) => {
            const productId = s?.iap?.productId || s?.iap?.subscriptionId || 'unknown'
            const provider = String(s?.payment?.provider || 'unknown').toLowerCase()
            const userId = s?.user?._id || s?.user || ''

            if (!acc[productId]) {
                acc[productId] = {
                    productId,
                    provider,
                    purchases: 0,
                    activeSubscriptions: 0,
                    buyerSet: new Set(),
                }
            }

            acc[productId].purchases += 1
            if (s?.status === 'active') {
                acc[productId].activeSubscriptions += 1
            }
            if (userId) {
                acc[productId].buyerSet.add(userId)
            }
            return acc
        }, {})
    )
        .map((row) => ({
            ...row,
            uniqueBuyers: row.buyerSet.size,
        }))
        .sort((a, b) => b.purchases - a.purchases)
    const googleSubscriptions = storeSubscriptions.filter(
        (s) => String(s?.payment?.provider || '').toLowerCase() === 'google'
    )
    const uniqueStoreBuyers = new Set(
        storeSubscriptions
            .map((s) => s?.user?._id || s?.user || '')
            .filter(Boolean)
    ).size
    const recordedRevenue = storeSubscriptions.reduce(
        (sum, s) => sum + resolveSubscriptionRevenue(s),
        0,
    )
    const activeStoreRevenue = storeSubscriptions
        .filter((s) => s?.status === 'active')
        .reduce((sum, s) => sum + resolveSubscriptionRevenue(s), 0)
    const pendingPriceCount = storeSubscriptions.filter((s) => {
        const resolved = resolveSubscriptionPrice(s)
        return resolved?.source === 'unknown'
    }).length

    const handlePageChange = (page) => {
        setCurrentPage(page)
    }

    function getUserName(userId) {
        if (typeof userId === 'object' && userId?.name) {
            return userId.name
        }
        const user = users.find(u => u._id === userId || u.id === userId)
        return user?.name || (typeof userId === 'object' && userId?.email ? userId.email.split('@')[0] : 'Unknown User')
    }

    function getUserEmail(userId) {
        if (typeof userId === 'object' && userId?.email) {
            return userId.email
        }
        const user = users.find(u => u._id === userId || u.id === userId)
        return user?.email || ''
    }

    // Calculate the current items for the page
    const indexOfLastItem = currentPage * itemsPerPage
    const indexOfFirstItem = indexOfLastItem - itemsPerPage

    const filteredSubscriptions = subscriptions.filter((subscription) => {
        const query = searchQuery.toLowerCase().trim()
        const userName = getUserName(subscription.user).toLowerCase()
        const userEmail = getUserEmail(subscription.user).toLowerCase()
        const planName = String(subscription.plan?.displayName || '').toLowerCase()
        const status = String(subscription.status || '').toLowerCase()
        const orderId = String(subscription.iap?.orderId || subscription.payment?.orderId || '').toLowerCase()
        const productId = String(subscription.iap?.productId || subscription.iap?.subscriptionId || '').toLowerCase()
        const provider = String(subscription?.payment?.provider || '').toLowerCase()

        const matchesSearch = !query || (
            userName.includes(query) ||
            userEmail.includes(query) ||
            planName.includes(query) ||
            status.includes(query) ||
            orderId.includes(query) ||
            productId.includes(query) ||
            provider.includes(query)
        )
        const matchesStatus = !statusFilter || subscription.status === statusFilter

        return matchesSearch && matchesStatus
    })

    const currentItems = filteredSubscriptions.slice(indexOfFirstItem, indexOfLastItem)

    const fetchSubscriptions = async () => {
        try {
            setLoading(true)
            const response = await getSubscriptions()
            if (response?.data?.success) {
                setSubscriptions(response?.data?.data || [])
            }
        } catch (error) {
            console.log("Error fetching subscriptions", error.message)
            message.error("Error fetching subscriptions")
        } finally {
            setLoading(false)
        }
    }

    const fetchUsers = async () => {
        try {
            const response = await getUsers()
            if (response?.data?.success) {
                setUsers(response?.data?.data || [])
            }
        } catch (error) {
            console.log("Error fetching users", error.message)
        }
    }

    useEffect(() => {
        fetchSubscriptions()
        fetchUsers()
    }, [])

    const handleViewSubscription = (subscription) => {
        setSelectedSubscription(subscription)
        setShowViewSubscription(true)
    }

    return (
        <div className="p-6 mt-16">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">Subscription Management</h2>
                    <p className="text-gray-600 mt-1">
                        In-app purchase monitoring only (Google Play / Apple App Store)
                    </p>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-blue-100 text-sm">Original App Products</p>
                            <p className="text-2xl font-bold">{originalStoreProducts.length}</p>
                        </div>
                        <div className="text-blue-200 text-2xl">
                            <BsCreditCard />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-green-100 text-sm">Store Purchases</p>
                            <p className="text-2xl font-bold">{storeSubscriptions.length}</p>
                        </div>
                        <div className="text-green-200 text-2xl">
                            <AiOutlineEye />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-purple-100 text-sm">Top Product</p>
                            <p className="text-2xl font-bold">
                                {Object.keys(productPurchaseCounts)[0] || 'N/A'}
                            </p>
                        </div>
                        <div className="text-purple-200 text-2xl">
                            <BsCreditCard />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-orange-100 text-sm">Active Store Subs</p>
                            <p className="text-2xl font-bold">
                                {storeSubscriptions.filter((s) => s?.status === 'active').length}
                            </p>
                        </div>
                        <div className="text-orange-200 text-2xl">
                            <BsCreditCard />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sky-100 text-sm">Google Pay Purchases</p>
                            <p className="text-2xl font-bold">{googleSubscriptions.length}</p>
                        </div>
                        <div className="text-sky-200 text-2xl">
                            <BsCreditCard />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-emerald-100 text-sm">Unique Buyers</p>
                            <p className="text-2xl font-bold">{uniqueStoreBuyers}</p>
                        </div>
                        <div className="text-emerald-200 text-2xl">
                            <AiOutlineEye />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-rose-500 to-rose-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-rose-100 text-sm">Recorded Store Revenue</p>
                            <p className="text-2xl font-bold">₹{recordedRevenue.toFixed(0)}</p>
                            <p className="text-rose-100 text-xs mt-1">
                                Active: ₹{activeStoreRevenue.toFixed(0)}
                                {pendingPriceCount > 0
                                    ? ` · ${pendingPriceCount} awaiting price sync`
                                    : ''}
                            </p>
                        </div>
                        <div className="text-rose-200 text-2xl">
                            <BsCreditCard />
                        </div>
                    </div>
                </div>
            </div>

            {/* Original store plans view */}
            <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Original Store Plans (Product IDs)</h3>
                {storePlanView.length === 0 ? (
                    <p className="text-sm text-gray-500">No Google/Apple product IDs found in purchases yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Store</th>
                                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Original Product ID</th>
                                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Store Price</th>
                                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Total Purchases</th>
                                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Active Subscriptions</th>
                                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Unique Buyers</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {storePlanView.map((planRow, index) => {
                                    const sampleSub = storeSubscriptions.find(
                                        (s) => (s?.iap?.productId || s?.iap?.subscriptionId) === planRow.productId
                                    )
                                    const samplePrice = sampleSub ? resolveSubscriptionPrice(sampleSub) : null
                                    return (
                                    <tr key={planRow.productId}>
                                        <td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900 capitalize">{planRow.provider}</td>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 break-all">{planRow.productId}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                            {samplePrice?.label || 'N/A'}
                                            {samplePrice?.source === 'unknown' && (
                                                <span className="block text-xs text-amber-600">
                                                    open Subscription screen in app to sync store price
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{planRow.purchases}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{planRow.activeSubscriptions}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{planRow.uniqueBuyers}</td>
                                    </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-3 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name, email, order ID, product ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Status</label>
                        <Select
                            value={statusFilter}
                            onChange={setStatusFilter}
                            className="w-full"
                            allowClear
                        >
                            <Option value="">All Status</Option>
                            <Option value="pending">Pending</Option>
                            <Option value="active">Active</Option>
                            <Option value="canceled">Canceled</Option>
                            <Option value="expired">Expired</Option>
                        </Select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setSearchQuery("")
                                setStatusFilter("")
                            }}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition duration-200 w-full"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Subscriptions Table */}
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price Paid</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Store Source</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Google Live Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {currentItems?.length > 0 ? (
                                    currentItems?.map((subscription, index) => {
                                        const priceInfo = resolveSubscriptionPrice(subscription)
                                        return (
                                        <tr key={subscription._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {(currentPage - 1) * itemsPerPage + index + 1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <div className="flex flex-col">
                                                    <span className="font-semibold text-gray-900">{getUserName(subscription.user)}</span>
                                                    {getUserEmail(subscription.user) ? (
                                                        <span className="text-xs text-gray-500 font-normal mt-0.5">{getUserEmail(subscription.user)}</span>
                                                    ) : null}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {subscription.plan?.displayName || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{priceInfo.label}</span>
                                                    {priceInfo.source === 'google_live' && (
                                                        <span className="text-xs text-gray-500">Google Play live</span>
                                                    )}
                                                    {priceInfo.source === 'unknown' && (
                                                        <span className="text-xs text-amber-600">Awaiting app price sync</span>
                                                    )}
                                                    {priceInfo.source === 'store_offer_sync' && (
                                                        <span className="text-xs text-gray-500">From app store listing</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <div className="flex flex-col">
                                                    <span className={`px-2 py-1 text-xs font-medium rounded-full w-fit ${
                                                        String(subscription?.payment?.provider || '').toLowerCase() === 'google'
                                                            ? 'bg-green-100 text-green-800'
                                                            : String(subscription?.payment?.provider || '').toLowerCase() === 'apple'
                                                                ? 'bg-gray-100 text-gray-800'
                                                                : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                        {subscription?.payment?.provider || 'manual'}
                                                    </span>
                                                    {(subscription?.iap?.productId || subscription?.iap?.subscriptionId) && (
                                                        <span className="text-xs text-gray-500 mt-1">
                                                            {subscription?.iap?.productId || subscription?.iap?.subscriptionId}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {String(subscription?.payment?.provider || '').toLowerCase() === 'google' ? (
                                                    subscription?.storeLive?.ok ? (
                                                        <div className="flex flex-col">
                                                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800 w-fit">
                                                                {subscription?.storeLive?.data?.subscriptionState || 'ACTIVE'}
                                                            </span>
                                                            {subscription?.storeLive?.data?.expiryTime && (
                                                                <span className="text-xs text-gray-500 mt-1">
                                                                    Exp: {new Date(subscription.storeLive.data.expiryTime).toLocaleString()}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                                                            Not available
                                                        </span>
                                                    )
                                                ) : (
                                                    <span className="text-xs text-gray-500">N/A</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                                    subscription.status === 'active' ? 'bg-green-100 text-green-800' :
                                                    subscription.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                    subscription.status === 'canceled' ? 'bg-red-100 text-red-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {subscription.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(subscription.startsAt).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(subscription.endsAt).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => handleViewSubscription(subscription)}
                                                        className="text-blue-600 hover:text-blue-900 p-1"
                                                        title="View Details"
                                                    >
                                                        <AiOutlineEye className="text-lg" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        )
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                                            {loading ? "Loading subscriptions..." : "No subscriptions found"}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {filteredSubscriptions.length > itemsPerPage && (
                <div className="flex justify-center mt-6">
                    <Pagination
                        current={currentPage}
                        total={filteredSubscriptions.length}
                        pageSize={itemsPerPage}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                        showQuickJumper
                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} subscriptions`}
                    />
                </div>
            )}

            {/* View Subscription Modal */}
            {showViewSubscription && (
                <Modal
                    title="Subscription Details"
                    open={showViewSubscription}
                    onCancel={() => setShowViewSubscription(false)}
                    footer={[
                        <button
                            key="close"
                            onClick={() => setShowViewSubscription(false)}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                        >
                            Close
                        </button>
                    ]}
                    width={600}
                >
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-500">User</p>
                                <p className="font-semibold text-gray-900">{getUserName(selectedSubscription.user)}</p>
                                {getUserEmail(selectedSubscription.user) ? (
                                    <p className="text-xs text-blue-600 font-medium break-all mt-0.5">{getUserEmail(selectedSubscription.user)}</p>
                                ) : null}
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Plan</p>
                                <p className="font-medium">{selectedSubscription.plan?.displayName}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Status</p>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    selectedSubscription.status === 'active' ? 'bg-green-100 text-green-800' :
                                    selectedSubscription.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                    selectedSubscription.status === 'canceled' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                }`}>
                                    {selectedSubscription.status}
                                </span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Price Paid (Store)</p>
                                <p className="font-medium">{resolveSubscriptionPrice(selectedSubscription).label}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Catalog Plan Price</p>
                                <p className="font-medium text-gray-600">
                                    {selectedSubscription.plan?.currency} {selectedSubscription.plan?.price}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Store Provider</p>
                                <p className="font-medium">{selectedSubscription?.payment?.provider || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Product ID</p>
                                <p className="font-medium break-all">{selectedSubscription?.iap?.productId || selectedSubscription?.iap?.subscriptionId || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Order ID</p>
                                <p className="font-medium break-all">{selectedSubscription?.iap?.orderId || selectedSubscription?.payment?.orderId || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Purchase Token</p>
                                <p className="font-medium break-all">{selectedSubscription?.iap?.purchaseToken || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Google Live Subscription State</p>
                                <p className="font-medium">
                                    {selectedSubscription?.storeLive?.ok
                                        ? (selectedSubscription?.storeLive?.data?.subscriptionState || 'ACTIVE')
                                        : (selectedSubscription?.storeLive?.error || 'N/A')}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Google Live Expiry</p>
                                <p className="font-medium">
                                    {selectedSubscription?.storeLive?.ok && selectedSubscription?.storeLive?.data?.expiryTime
                                        ? new Date(selectedSubscription.storeLive.data.expiryTime).toLocaleString()
                                        : 'N/A'}
                                </p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-500">Start Date</p>
                                <p className="font-medium">{new Date(selectedSubscription.startsAt).toLocaleDateString()}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">End Date</p>
                                <p className="font-medium">{new Date(selectedSubscription.endsAt).toLocaleDateString()}</p>
                            </div>
                        </div>
                        {selectedSubscription.cancelAtPeriodEnd && (
                            <div>
                                <p className="text-sm text-gray-500">Cancel at Period End</p>
                                <p className="font-medium text-orange-600">Yes</p>
                            </div>
                        )}
                        {selectedSubscription.canceledAt && (
                            <div>
                                <p className="text-sm text-gray-500">Canceled At</p>
                                <p className="font-medium">{new Date(selectedSubscription.canceledAt).toLocaleDateString()}</p>
                            </div>
                        )}
                    </div>
                </Modal>
            )}

            {loading && <Load />}
        </div>
    )
}

export default AdminSubscriptions
